import { SIMPLE_RESERVATION_EMAIL_LANGUAGE, COMPONENT_VALUE_TYPES as types } from 'shared/constants';
import type { PropsSchemaList } from 'shared/types/PropsSchema';

interface FormOptions {
  required: boolean;
  label: string;
}

interface ReferralOptions {
  required: boolean;
  label: string;
  referralCodeMessage: string;
  errorIncorrectReferralCode: string;
  errorSelfReferralCode: string;
}

export interface Props {
  editor?: object;
  backgroundImage: {
    fullScreen: boolean;
    image: string;
  }[];
  header: {
    title: string;
    description: string;
  };
  form: {
    firstName: FormOptions;
    lastName: FormOptions;
    phone: {
      required: boolean;
      label: string;
      list: {
        text: string;
        value: string;
      }[];
    };
    country: {
      required: boolean;
      label: string;
      list: {
        text: string;
        value: string;
      }[];
    };
    email: FormOptions;
    referralCode: ReferralOptions;
  };
  footer: {
    buttonText: string;
    disclaimerText: string;
  };
  successModal: {
    header: string;
    body: string;
    button: {
      buttonText: string;
      buttonLink: string;
    };
  };
  emailSettings: {
    emailLanguage: string;
  };
}

const propsSchema: PropsSchemaList = [
  {
    key: 'backgroundImage',
    label: 'Images',
    tooltip: 'Add multiple images to turn this into a carousel',
    type: types.ARRAY,
    children: [
      {
        key: 'fullScreen',
        label: 'Full Screen Image',
        tooltip: 'Makes the image fill the height and width of the page',
        type: types.BOOLEAN,
      },
      {
        key: 'image',
        label: 'Image',
        type: types.S3_ASSET_URL,
      },
    ],
  },
  {
    key: 'header',
    label: 'Header',
    type: types.OBJECT,
    children: [
      {
        key: 'title',
        label: 'Title',
        type: types.TINYMCE,
        options: {
          menubar: false,
          toolbar: 'fontsizeselect fontWeight lineheight forecolor',
          height: 150,
        }
      },
      {
        key: 'description',
        label: 'Description',
        type: types.TINYMCE,
        options: {
          menubar: false,
          toolbar: 'fontsizeselect fontWeight italic underline link lineheight forecolor',
          height: 200,
        }
      },
    ],
  },
  {
    key: 'form',
    label: 'Reservation Form',
    type: types.OBJECT,
    children: [
      {
        key: 'firstName',
        label: 'First Name',
        type: types.OBJECT,
        children: [
          {
            key: 'required',
            label: 'Required Field',
            type: types.BOOLEAN,
          },
          {
            key: 'label',
            label: 'First Name Text',
            type: types.TEXT,
          },
          
        ]
      },
      {
        key: 'lastName',
        label: 'Last Name',
        type: types.OBJECT,
        children: [
          {
            key: 'required',
            label: 'Required Field',
            type: types.BOOLEAN,
          },
          {
            key: 'label',
            label: 'Last Name Text',
            type: types.TEXT,
          },
        ]
      },
      {
        key: 'phone',
        label: 'Phone',
        type: types.OBJECT,
        children: [
          {
            key: 'required',
            label: 'Required Field',
            type: types.BOOLEAN,
          },
          {
            key: 'label',
            label: 'Phone Text',
            type: types.TEXT,
          },
          {
            key: 'list',
            label: 'List of area codes',
            type: types.ARRAY,
            children: [
              {
                key: 'text',
                label: 'Area Code Name',
                type: types.TEXT,
              },
              {
                key: 'value',
                label: 'Area Code Number',
                type: types.TEXT,
              },
            ]
          },
        ]
      },
      {
        key: 'country',
        label: 'Country',
        type: types.OBJECT,
        children: [
          {
            key: 'required',
            label: 'Required Field',
            type: types.BOOLEAN,
          },
          {
            key: 'label',
            label: 'Country Text',
            type: types.TEXT,
          },
          {
            key: 'list',
            label: 'List of Countries',
            type: types.ARRAY,
            children: [
              {
                key: 'text',
                label: 'Country Name',
                type: types.TEXT,
              },
              {
                key: 'value',
                label: 'Country Name Translation',
                tooltip: 'This is not displayed to the user. Please translate the country name to your desired language.',
                type: types.TEXT,
              },
            ]
          },
        ]
      },
      {
        key: 'email',
        label: 'Email',
        type: types.OBJECT,
        children: [
          {
            key: 'required',
            label: 'Required Field',
            type: types.BOOLEAN,
          },
          {
            key: 'label',
            label: 'Email Text',
            type: types.TEXT,
          },
        ]
      },
      {
        key: 'referralCode',
        label: 'Referral Code',
        type: types.OBJECT,
        children: [
          {
            key: 'required',
            label: 'Required Field',
            type: types.BOOLEAN,
          },
          {
            key: 'label',
            label: 'Referral Code Text',
            type: types.TEXT,
          },
          {
            key: 'referralCodeMessage',
            label: 'General Text: Message that goes under the referral code field',
            type: types.TEXT,
          },
          {
            key: 'errorIncorrectReferralCode',
            label: 'Error Text: Incorrect Referral Code',
            type: types.TEXT,
          },
          {
            key: 'errorSelfReferralCode',
            label: 'Error Text: Cannot Use Your Own Referral Code',
            type: types.TEXT,
          },
        ]
      },
    ],
  },
  {
    key: 'footer',
    label: 'Footer',
    type: types.OBJECT,
    children: [
      {
        key: 'buttonText',
        label: 'Submit Button Text',
        type: types.TEXT,
      },
      {
        key: 'disclaimerText',
        label: 'Disclaimer Text',
        type: types.TINYMCE,
        options: {
          menubar: false,
          toolbar: 'fontsizeselect fontWeight italic underline link lineheight forecolor',
          height: 200,
        },
      },
    ],
  },
  {
    key: 'successModal',
    label: 'Success Modal',
    type: types.OBJECT,
    children: [
      {
        key: 'header',
        label: 'Header',
        type: types.TEXT,
      },
      {
        key: 'body',
        label: 'Body',
        type: types.TEXT,
      },
      {
        key: 'button',
        label: 'Button',
        type: types.OBJECT,
        children: [
          {
            key: 'buttonText',
            label: 'Text',
            type: types.TEXT,
          },
          {
            key: 'buttonLink',
            label: 'Link',
            type: types.TEXT,
          },
        ]
      },
    ],
  },
  {
    key: 'emailSettings',
    label: 'Email Settings',
    type: types.OBJECT,
    children: [
      {
        key: 'emailLanguage',
        label: "Pick what language the customer's email will be in",
        type: types.ENUM,
        options: SIMPLE_RESERVATION_EMAIL_LANGUAGE,
      },
    ],
  },
];

const defaultProps: Props = {
  backgroundImage: [
    { image: 'https://genesis-cdn.ff.com/configurator/design/images/exterior/3840x2160/FF91_Futurist_IceWhite_S.png', fullScreen: true },
  ],
  header: {
    title: 'Reserve',
    description: 'FF 91 2.0 can now be reserved as it will soon be launched in the United Arab Emirates, Qatar, and Saudi Arabia.',
  },
  form: {
    firstName: {
      required: false,
      label: 'First Name',
    },
    lastName: {
      required: true,
      label: 'Last Name',
    },
    phone: {
      required: false,
      label: 'Phone',
      list: [
        { text: '+971 United Arab Emirates', value: '+971' },
        { text: '+966 Saudi Arabia', value: '+966' },
      ],
    },
    country: {
      required: true,
      label: 'Country',
      list: [
        { text: 'United Arab Emirates', value: 'United Arab Emirates' },
        { text: 'Saudi Arabia', value: 'Saudi Arabia' },
      ]
    },
    email: {
      required: true,
      label: 'Email',
    },
    referralCode: {
      required: false,
      label: 'Referral Code',
      referralCodeMessage: 'By using the referral code to make your booking',
      errorIncorrectReferralCode: 'The referral code is incorrect. Please verify your referral code and re-enter, or delete the referral code.',
      errorSelfReferralCode: 'According to our referral rules, you cannot use your own referral code for reserve. Please use the referral code provided by someone else, or delete the referral code.'
    },
  },
  footer: {
    buttonText: 'Submit',
    disclaimerText: 'By clicking "Submit," I consent to FF contacting me regarding this request using the provided contact information. I acknowledge that calls or texts may be made using automatic or computer-assisted dialing, or pre-recorded messages. Standard message and data rates apply.'
  },
  successModal: {
    header: 'Reservation Successful',
    body: 'Thank you very much for your reservation. We will prioritize your purchase when it is issued.',
    button: {
      buttonText: 'Go to the home page',
      buttonLink: 'https://www.ff.com',
    },
  },
  emailSettings: {
    emailLanguage: 'AR',
  },
};

export default {
  title: 'Simple Reservation',
  id: 'SimpleReservation',
  propsSchema,
  defaultProps,
}
